import { useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';

import {
  usePartners,
  useCreatePartner,
  useUpdatePartner,
  useDeletePartner,
  PartnerWithTotals,
} from '../api/useCosts';
import { formatUsd } from '../utils/format';

const fmtDate = (value: string | undefined | null) => {
  if (!value) return '-';
  return new Date(value).toISOString().split('T')[0];
};

export const PartnersPanel = () => {
  const { data: partners } = usePartners();
  const { mutate: createPartner, isLoading: creating } = useCreatePartner();
  const { mutate: updatePartner } = useUpdatePartner();
  const { mutate: deletePartner } = useDeletePartner();

  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<PartnerWithTotals | null>(
    null
  );

  // Escape unmounts the input, which still fires a blur — this skips the
  // commit that blur would otherwise run.
  const cancelledRef = useRef(false);

  const rows = useMemo(() => {
    return [...(partners ?? [])].sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [partners]);

  const canAdd = name.trim().length > 0;

  const handleAdd = () => {
    if (!canAdd || creating) return;
    createPartner({ name: name.trim() }, { onSuccess: () => setName('') });
  };

  const startEdit = (partner: PartnerWithTotals) => {
    cancelledRef.current = false;
    setEditingId(partner._id);
    setDraftName(partner.name);
  };

  const cancelEdit = () => {
    cancelledRef.current = true;
    setEditingId(null);
    setDraftName('');
  };

  const commitEdit = (partner: PartnerWithTotals) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const next = draftName.trim();
    setEditingId(null);
    setDraftName('');
    if (!next || next === partner.name) return;
    updatePartner({ id: partner._id, name: next });
  };

  return (
    <Paper sx={{ p: 1.5, mt: 2 }}>
      <Typography
        sx={{
          fontSize: '0.72rem',
          textTransform: 'uppercase',
          color: 'grey.600',
          mb: 1,
        }}
      >
        Partners
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        mb={1}
      >
        <TextField
          size="small"
          label="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleAdd();
          }}
          sx={{ minWidth: 220 }}
        />
        <Button
          variant="contained"
          size="small"
          disabled={!canAdd || creating}
          onClick={handleAdd}
        >
          Add
        </Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Total Received</TableCell>
            <TableCell align="right"># Distributions</TableCell>
            <TableCell>Last Received</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(partner => (
            <TableRow
              key={partner._id}
              sx={{ opacity: partner.active ? 1 : 0.6 }}
            >
              <TableCell>
                {editingId === partner._id ? (
                  <TextField
                    size="small"
                    value={draftName}
                    autoFocus
                    onChange={e => setDraftName(e.target.value)}
                    onBlur={() => commitEdit(partner)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitEdit(partner);
                      else if (e.key === 'Escape') cancelEdit();
                    }}
                    sx={{ minWidth: 180 }}
                  />
                ) : (
                  <Typography
                    component="span"
                    sx={{ fontSize: '0.82rem', cursor: 'pointer' }}
                    onClick={() => startEdit(partner)}
                  >
                    {partner.name}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                {partner.active ? 'Active' : 'Inactive'}
              </TableCell>
              <TableCell align="right">
                {formatUsd(partner.totalReceivedUsd)}
              </TableCell>
              <TableCell align="right">{partner.distributionCount}</TableCell>
              <TableCell>{fmtDate(partner.lastReceivedAt)}</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>
                <IconButton
                  size="small"
                  title="Rename"
                  onClick={() => startEdit(partner)}
                >
                  <EditOutlinedIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
                <IconButton
                  size="small"
                  title={partner.active ? 'Deactivate' : 'Activate'}
                  onClick={() =>
                    updatePartner({ id: partner._id, active: !partner.active })
                  }
                >
                  {partner.active ? (
                    <ToggleOnIcon sx={{ fontSize: '1.1rem' }} />
                  ) : (
                    <ToggleOffIcon sx={{ fontSize: '1.1rem' }} />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  title="Delete"
                  onClick={() => setConfirmDelete(partner)}
                >
                  <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {partners && partners.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                  No partners yet
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '0.95rem' }}>Delete partner?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.82rem' }}>
            {confirmDelete?.name} — {formatUsd(confirmDelete?.totalReceivedUsd)}{' '}
            received across {confirmDelete?.distributionCount ?? 0}{' '}
            distributions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={() => {
              if (confirmDelete) {
                deletePartner(confirmDelete._id);
                setConfirmDelete(null);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
